import i18n from "../../../i18n";

import { STATUS_HEX as _SH, STATUS_HEX_DARK as _SHD } from "../../../components/analytics/theme";
import type { OfficeStatus } from "../api/types";

/**
 * "6h 42m", "42m", "less than a minute" — and their Arabic equivalents.
 *
 * These read the i18next instance directly rather than taking a `t`, because
 * they are called from twenty-odd places, most of them deep inside a row or a
 * cell. Threading a translator through all of them to append one letter would
 * be worse than this import, and the unit is genuinely part of formatting the
 * number rather than of the sentence around it.
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 60) return i18n.t("homework.units.underMinute");
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const h = i18n.t("homework.units.hours");
  const m = i18n.t("homework.units.minutes");
  if (hours === 0) return `${minutes}${m}`;
  if (minutes === 0) return `${hours}${h}`;
  return `${hours}${h} ${minutes}${m}`;
}

/**
 * "3h 40m", "0h 00m" — the design's fixed-shape "worked today" figure for the
 * people list. Always both units with padded minutes, so a column of them
 * lines up and zero reads as zero rather than as "less than a minute" (which,
 * next to an Offline badge, looked like "last seen a minute ago").
 */
export function formatHoursMinutes(seconds: number): string {
  const safe = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const h = i18n.t("homework.units.hours");
  const m = i18n.t("homework.units.minutes");
  return `${hours}${h} ${String(minutes).padStart(2, "0")}${m}`;
}

/** "6:42" — for the ticking session counter, where a stable width matters. */
export function formatClock(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}

export function formatHours(seconds: number): string {
  return `${(seconds / 3600).toFixed(1)}${i18n.t("homework.units.hours")}`;
}

/**
 * Status names live in the locale files, not here.
 *
 * The map is of keys rather than words because this module is not React and
 * cannot call `t` — every call site translates, so a status reads the same
 * whether it is on a badge, in a menu or inside a sentence.
 */
export const STATUS_LABEL_KEYS: Record<OfficeStatus, string> = {
  available: "homework.status.available",
  focus: "homework.status.focus",
  meeting: "homework.status.meeting",
  break: "homework.status.break",
  away: "homework.status.away",
  offline: "homework.status.offline",
};

/**
 * Status colours as a low-alpha wash plus a ring in the same hue — tint, never
 * a solid fill, so the one saturated element in a view stays meaningful.
 *
 * The tint is theme-agnostic (10% of a mid hue reads as a wash over white and
 * over near-black alike), but the text on top of it is not: a single mid-tone
 * ink cannot clear 4.5:1 against both. So the ink is stated per theme — the
 * deep shade in light, the pale one in dark — which is what `HW_TINT` in
 * `lib/tint.ts` exists to keep consistent everywhere else in the module.
 */
export const STATUS_CLASSES: Record<OfficeStatus, string> = {
  available: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-inset ring-emerald-500/30",
  focus: "bg-violet-500/10 text-violet-700 dark:text-violet-300 ring-1 ring-inset ring-violet-500/30",
  meeting: "bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-500/30",
  break: "bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-1 ring-inset ring-sky-500/30",
  away: "bg-slate-500/10 text-slate-600 dark:text-slate-300 ring-1 ring-inset ring-slate-500/30",
  offline: "bg-muted text-muted-foreground ring-1 ring-inset ring-border",
};

/**
 * Canvas fills for the floor renderer — the same hues, resolved to hex.
 *
 * The status ring is a saturated disc behind an avatar rather than text, so one
 * value works on both floors; only the two greys need a theme, because slate at
 * the weight that reads on white disappears into near-black.
 */
export const STATUS_HEX = _SH as Record<OfficeStatus, string>;
export const STATUS_HEX_DARK = _SHD as Record<OfficeStatus, string>;

/** Deterministic avatar hue from a user id, so a person looks the same everywhere. */
export function avatarHue(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) % 360;
  }
  return hash;
}

/**
 * The two gradient stops and the ink for a person's generated avatar.
 *
 * The hue comes from a user id, so it sweeps the whole wheel and a single ink
 * cannot serve it: white initials on the yellow-green quarter measured about
 * 1.8:1 before this existed. Two ramps rather than one, chosen by the hue's own
 * luminance — a bright disc with near-black initials where the hue is naturally
 * light, a deep disc with white ones where it is not — so every person is
 * legible without narrowing the palette to the hues that happen to be dark.
 *
 * The *lighter* stop decides it, not the average: the radial highlight sits in
 * the top-left corner exactly where the first letter is.
 */
export interface AvatarPaint {
  /** Highlight stop, top-left of the radial gradient. */
  from: string;
  /** Base stop. */
  to: string;
  /** Initials colour. */
  ink: string;
}

/** Near-black rather than pure black, matching the suite's own foreground. */
const AVATAR_DARK_INK = "#0a0a0a";
const AVATAR_LIGHT_INK = "#ffffff";
/** Relative luminance of `AVATAR_DARK_INK`. */
const DARK_INK_LUMINANCE = 0.00304;

/**
 * Two ramps: a bright disc that takes near-black initials, and a deep one that
 * takes white. Their lightness is not a taste call — these values are where a
 * sweep of all 360 hues has the most headroom, bottoming out at 5.4:1 instead
 * of the 1.8:1 a single bright ramp with white ink produced on yellow-green.
 */
const BRIGHT_RAMP = { from: 72, to: 52 };
const DEEP_RAMP = { from: 40, to: 28 };

export function avatarPaint(hue: number): AvatarPaint {
  // Each ramp is judged on its worst stop — dark ink struggles on the darker
  // one, white ink on the lighter one — and the better of the two wins. No
  // threshold to tune: the arithmetic picks the ink.
  const darkInkWorst =
    (relativeLuminanceOfHsl(hue, 72, BRIGHT_RAMP.to) + 0.05) / (DARK_INK_LUMINANCE + 0.05);
  const lightInkWorst = 1.05 / (relativeLuminanceOfHsl(hue, 78, DEEP_RAMP.from) + 0.05);

  if (darkInkWorst >= lightInkWorst) {
    return {
      from: `hsl(${hue} 85% ${BRIGHT_RAMP.from}%)`,
      to: `hsl(${hue} 72% ${BRIGHT_RAMP.to}%)`,
      ink: AVATAR_DARK_INK,
    };
  }
  return {
    from: `hsl(${hue} 78% ${DEEP_RAMP.from}%)`,
    to: `hsl(${hue} 72% ${DEEP_RAMP.to}%)`,
    ink: AVATAR_LIGHT_INK,
  };
}

/** The gradient as a single CSS value, for a DOM avatar. */
export function avatarGradient(paint: AvatarPaint): string {
  return `radial-gradient(circle at 32% 28%, ${paint.from}, ${paint.to})`;
}

export function relativeLuminanceOfHsl(h: number, s: number, l: number): number {
  const sat = s / 100;
  const light = l / 100;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = light - c / 2;
  const [r1, g1, b1] =
    hp < 1
      ? [c, x, 0]
      : hp < 2
        ? [x, c, 0]
        : hp < 3
          ? [0, c, x]
          : hp < 4
            ? [0, x, c]
            : hp < 5
              ? [x, 0, c]
              : [c, 0, x];
  const channel = (v: number) => {
    const n = v + m;
    return n <= 0.03928 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r1) + 0.7152 * channel(g1) + 0.0722 * channel(b1);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function isoDaysFromNow(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}
